import {isDrawnForProduct, templateOffersProduct} from "./templateManifest";
import {CHANNELS, layoutAmountFor} from "./products.js";
import {getProductBySlug, releasesSetsForProduct} from "./products/index.js";
import {getTemplateDefinition, type TemplateDefinition} from "./template-catalog";
import {RELEASES_SETS} from "./release-sets";
import {resolveLayoutOptions} from "./layout-options";

export type BannerSelection = {
  templateKey: string;
  product: string;
  contentType: string;
  set: string | null;
};

/**
 * Does a product's releases page draw the template on the layout? A .NET
 * product draws the .NET splashes (`drawnFor` / `notDrawnFor`). The upload
 * layouts 5 and 6 draw only the templates that say so (`uploadLayouts`),
 * whatever their `layouts`. The Releases route reads it too, so the two
 * cannot drift.
 */
export function isReleaseTemplateAvailable(
  template: Pick<TemplateDefinition, "uploadLayouts" | "layouts" | "drawnFor" | "notDrawnFor">,
  productCode: string,
  layout: number
) {
  if (!isDrawnForProduct(template, productCode)) return false;
  if (layout === 5 || layout === 6) return template.uploadLayouts === true;
  return !template.layouts || template.layouts.includes(layout);
}

export function resolveBannerSelection(selection: BannerSelection) {
  const definition = getTemplateDefinition(selection.templateKey);
  const product = getProductBySlug(selection.product);
  if (!product) throw new Error(`Unknown product: ${selection.product}`);
  if (product && !templateOffersProduct(definition, product.code)) throw new Error(`Unavailable template/product: ${selection.templateKey}/${selection.product}`);
  const channel = CHANNELS.find((entry) => entry.value === definition.channel);
  if (!channel) throw new Error(`Unknown channel: ${definition.channel}`);
  const type = channel.types.find((entry) => entry.value === selection.contentType);
  if (!type || type.value === "action" || !Object.hasOwn(type.products, product.code)) {
    throw new Error(`Unavailable product/content type: ${selection.product}/${selection.contentType}`);
  }
  if ((definition.set ?? null) !== selection.set) throw new Error(`Unavailable set: ${selection.set}`);
  if (definition.contentTypes && !definition.contentTypes.includes(selection.contentType)) {
    throw new Error(`Unavailable template/content type: ${selection.templateKey}/${selection.contentType}`);
  }
  const releaseSet = definition.channel === "releases"
    ? RELEASES_SETS.find((entry) => entry.value === selection.set) : undefined;
  if (definition.channel === "releases" && !releaseSet) throw new Error(`Unknown release set: ${selection.set}`);
  const amount = layoutAmountFor(definition.channel, product.code);
  const options = resolveLayoutOptions({
    channel: definition.channel, type: selection.contentType, set: selection.set ?? undefined,
    product: product.code, amount, layoutIcons: releaseSet?.layoutIcons,
  });
  // Families without a layout selector render their single canonical layout.
  const layouts = (options.length ? options.map((entry) => entry.id) : [1]).filter((layout) => {
    if (definition.channel === "releases" && !isReleaseTemplateAvailable(definition, product.code, layout)) return false;
    if (definition.layouts && !definition.layouts.includes(layout)) return false;
    const config = "layoutsConfig" in channel ? channel.layoutsConfig as Record<number, string[]> : undefined;
    return !config || Boolean(config[layout]?.includes(definition.id));
  });
  if (!layouts.length) throw new Error(`No available layouts: ${selection.templateKey}/${selection.product}`);
  return {definition, product, channel, type, layouts, layoutOptions: options.filter((option) => layouts.includes(option.id))};
}

// New selections follow the current offering. Saved documents retain their identities.
export function resolveOfferedBannerSelection(selection: BannerSelection) {
  const resolved = resolveBannerSelection(selection);
  if (resolved.definition.channel === "releases" && !releasesSetsForProduct(resolved.product.code).some((entry) => entry.value === selection.set)) {
    throw new Error(`Unavailable release set: ${selection.product}/${selection.set}`);
  }
  return resolved;
}

export function validateBannerCombination(selection: BannerSelection, layout: number) {
  const resolved = resolveBannerSelection(selection);
  // Grid v1 saved intro cards on layouts 1 and 2. Keep these snapshots readable.
  const legacyIntro = resolved.definition.key === "livestream-stories-intro" && [1, 2].includes(layout);
  if (!resolved.layouts.includes(layout) && !legacyIntro) throw new Error(`Unavailable layout: ${selection.templateKey}/${layout}`);
  return resolved;
}
